import { ThemeId } from "@iconics/components";
import { IAdminPortalRights, IApiRights, IDataModelRights, IFlexRights, IIBSSRights, IOneLens360Rights, IRoamerRights, IUserRights } from "@iconics/core";
import { IUserDetails, INodeData } from "./Models";

export class LocalStorageProvider implements ILocalStorageProvider
{
    private storageKey = 'e7da6caf-13df-42ca-8c07-539da7eb51c3';
    private key(value: string) { return `${value}.${this.storageKey}`; }

    public setUserDetails(value: IUserDetails): void
    {
        const email = value.email ? value.email.toLowerCase() : '';
        const userDetails = { ...value, email };
        localStorage["user"] = JSON.stringify(userDetails);
    }

    public getUserDetails(): IUserDetails
    {
        return JSON.parse(localStorage.getItem("user") || "{}");
    }

    public setToken(value: string): void
    {
        localStorage.setItem("token", value);
    }

    public getToken(): string
    {
        return localStorage.getItem("token") ?? "";
    }

    public setHubVersion(value: string): void
    {
        localStorage["hubVersion"] = value;
    }

    public getHubVersion(): string
    {
        return localStorage["hubVersion"] ?? "";
    }

    public setNodeData(value: INodeData[]): void
    {
        localStorage["nodeData"] = JSON.stringify(value);
    }

    public getNodeData(): INodeData[]
    {
        return JSON.parse(localStorage["nodeData"] || "[]");
    };

    public setIbssRights(value: IUserRights): void
    {
        localStorage.setItem("ibssRightList", JSON.stringify(value));
    }

    public getIbssRightList(): IUserRights
    {
        return JSON.parse(localStorage.getItem("ibssRightList") || "{}");
    }

    public setUserPolicy(value: boolean): void
    {
        localStorage.setItem("userPolicyAccepted", JSON.stringify(value));
    }

    public getUserPolicy(): boolean
    {
        return JSON.parse(localStorage.getItem("userPolicyAccepted") || "false");
    }

    public getTenantId(): string
    {
        return localStorage.getItem(this.key('tenantId')) ?? '';
    }

    public setTenantId(value: string): void
    {
        localStorage.setItem(this.key('tenantId'), value);
    }

    public setTheme(value: ThemeId): void
    {
        localStorage.setItem('theme', value);
    }

    public getTheme(): ThemeId | undefined
    {
        const themeId = localStorage.getItem('theme');
        return themeId as ThemeId ?? undefined;
    }

    public setAadConfig(value: IAadConfig): void
    {
        localStorage['aadConfig'] = JSON.stringify(value);
    }

    public getAadConfig(): IAadConfig
    {
        try
        {
            return JSON.parse(localStorage['aadConfig']);
        }
        catch
        {
            return {
                clientId: '',
                tenantDomain: '',
            };
        }
    }

    public setAuth0Config(value: IAuth0Config): void
    {
        localStorage['auth0Config'] = JSON.stringify(value);
    }

    public getAuth0Config(): IAuth0Config
    {
        try
        {
            return JSON.parse(localStorage['auth0Config']);
        }
        catch
        {
            return {
                clientId: '',
                domain: '',
            };
        }
    }

    public hasRight(name: string, buildingId?: number): boolean
    {
        const parts = name.split(".");
        if (parts.length != 3)
        {
            throw new Error("Right must be in the format {module}.{entity}.{action}");
        }
        try
        {
            const module = parts[0] as keyof IUserRights;
            const entity = parts[1] as keyof (IAdminPortalRights | IApiRights | IDataModelRights | IFlexRights | IIBSSRights | IOneLens360Rights | IRoamerRights);
            const action = parts[2];

            const ibssRights = this.getIbssRightList();
            const userDetail = this.getUserDetails();
            const allRights = [userDetail.userRights, ibssRights];

            for (let rights of allRights)
            {
                const hasRight = ((rights[module][entity] as string[]).find((i: string) => i === action) !== undefined);
                if (hasRight && buildingId)
                {
                    const appliesToBuilding = this.permissionAppliesToBuilding(name, buildingId);
                    return appliesToBuilding;
                }
                else if (hasRight)
                {
                    return true;
                }
            }
            return false;
        }
        catch
        {
            return false;
        }
    }

    public permissionAppliesToBuilding(right: string, buildingId: number): boolean
    {
        const applicableBuildings = this.getIbssRightList().APPLIESTO[right] ?? [];
        const isBuildingApplicable = applicableBuildings.includes(buildingId.toString());
        return isBuildingApplicable;
    }

    public clear(): void
    {
        const version = this.getHubVersion();
        const theme = this.getTheme();
        localStorage.clear();
        this.setHubVersion(version);
        if (theme)
        {
            this.setTheme(theme);
        }
    }
}

export interface ILocalStorageProvider
{
    setUserDetails(value: IUserDetails): void;
    getUserDetails(): IUserDetails;
    setToken(value: string): void;
    getToken(): string;
    setHubVersion(value: string): void;
    getHubVersion(): string;
    setNodeData(value: INodeData[]): void;
    getNodeData(): INodeData[];
    setIbssRights(value: IUserRights): void;
    getIbssRightList(): IUserRights;
    setUserPolicy(value: boolean): void;
    getUserPolicy(): boolean;
    getTenantId(): string;
    setTenantId(value: string): void;
    setTheme(value: ThemeId): void;
    getTheme(): ThemeId | undefined;
    setAadConfig(value: IAadConfig): void;
    getAadConfig(): IAadConfig;
    setAuth0Config(value: IAuth0Config): void;
    getAuth0Config(): IAuth0Config;
    hasRight(name: string, buildingId?: number): boolean;
    permissionAppliesToBuilding(right: string, buildingId: number): boolean;
    clear(): void;
}

export interface IAadConfig
{
    clientId: string;
    tenantDomain: string;
}

export interface IAuth0Config
{
    clientId: string;
    domain: string;
}
